/* Heated Floor 3D scene — real WebGL (Three.js) for the kit builder.
   Driven by the builder via window.HFKBScene.update(data). Lazy, shadowed,
   reduced-motion aware, disposes cleanly. Coordinate space matches the builder's
   200x160 floor (open-floor FX rect x:20..180 y:16..144) mapped into a W x D world.

   Visual upgrades ported from the reference demo: real GLB fixture models (with
   procedural physical-material fallback), instanced 3D studs, ACES filmic tone
   mapping + env map + rim light, textured subfloor/membrane, crisp cable.
   The builder's logic + UI are untouched; only the renderer changed. */
import * as THREE from 'three';
import { GLTFLoader } from 'three/gltfloader';

(function () {
  var UNIT = 0.8, MAXW = 11, MIND = 2.0, REF_FIT = 14, WREF = 8 * 0.8;
  var W = 8, D = 6, currentSF = 1;
  var MEM_TOP = 0.18;

  var MODEL_URLS_KEY = { vanity: 'vanity', toilet: 'toilet', tub: 'tub', shower: 'shower', island: 'island', cabinets: 'cabinets', builtin: 'cabinets' };
  var MODEL_CONFIG = {
    toilet:   { maxHeight: 1.02 },
    tub:      { maxHeight: 1.05, scale: 1.04 },
    vanity:   { maxHeight: 1.35, scale: 1.06 },
    shower:   { maxHeight: 0.5,  scale: 0.34, offsetX: 0.38, offsetZ: -0.34 },
    cabinets: { maxHeight: 1.8,  scale: 1.04 },
    island:   { maxHeight: 1.45, scale: 1.04 }
  };
  var WALL_ROT = { top: Math.PI, bottom: 0, left: -Math.PI / 2, right: Math.PI / 2 };
  // per-model base Y rotation so the model FRONT points to +z (room interior on a back/top wall);
  // facing then flips +PI on a front/bottom wall. Tuned from the harness.
  var MODEL_FACE = { toilet: 0, tub: 0, vanity: Math.PI, shower: 0, island: 0, cabinets: Math.PI, builtin: Math.PI };

  var container, scene, camera, renderer, raf = null, running = false, reduced = false, inView = true;
  var floorGroup, fixturesGroup, cableGroup, duoMesh, subMesh, memMesh, memTex, studMesh, key, lastT = 0, spin = 0, mounted = false;
  var mats = {};
  var camDir = new THREE.Vector3(8.2, 7.6, 9.4).normalize();
  var loader, modelCache = {}, modelsRequested = false, lastData = null;

  function setFloorSize(longFt, shortFt) {
    longFt = longFt > 0 ? longFt : 8; shortFt = shortFt > 0 ? shortFt : 6;
    var w = longFt * UNIT, d = shortFt * UNIT, f;
    if (w > MAXW) { f = MAXW / w; w = MAXW; d *= f; }
    if (w < MIND) { f = MIND / w; w = MIND; d *= f; }
    w = Math.round(w * 100) / 100; d = Math.round(d * 100) / 100;
    if (Math.abs(w - W) < 0.02 && Math.abs(d - D) < 0.02) return false;
    W = w; D = d; currentSF = W / WREF;
    if (subMesh) { subMesh.geometry.dispose(); subMesh.geometry = new THREE.BoxGeometry(W + 0.6, 0.5, D + 0.6); }
    if (duoMesh) { duoMesh.geometry.dispose(); duoMesh.geometry = new THREE.BoxGeometry(W + 0.12, 0.16, D + 0.12); }
    if (memMesh) { memMesh.geometry.dispose(); memMesh.geometry = new THREE.BoxGeometry(W, 0.12, D); }
    if (memTex) memTex.repeat.set(Math.max(3, Math.round(W * 1.4)), Math.max(3, Math.round(D * 1.4)));
    buildStuds();
    return true;
  }
  function fitCamera() { if (!camera) return; camera.position.copy(camDir).multiplyScalar(REF_FIT); camera.lookAt(0, 0.25, 0); }

  function mapX(sx) { return (sx - 100) / 160 * W; }
  function mapZ(sy) { return (sy - 80) / 128 * D; }
  function fxScaleX(w) { return w / 160 * W; }
  function fxScaleZ(h) { return h / 128 * D; }

  /* ---------- procedural textures ---------- */
  function membraneTexture() {
    var c = document.createElement('canvas'); c.width = c.height = 512; var g = c.getContext('2d');
    g.fillStyle = '#e8662a'; g.fillRect(0, 0, 512, 512);
    g.strokeStyle = 'rgba(95,37,15,0.13)'; g.lineWidth = 2;
    for (var i = 0; i <= 512; i += 32) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, 512); g.stroke(); g.beginPath(); g.moveTo(0, i); g.lineTo(512, i); g.stroke(); }
    var t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; return t;
  }
  function woodTexture() {
    var c = document.createElement('canvas'); c.width = 512; c.height = 256; var g = c.getContext('2d');
    var grd = g.createLinearGradient(0, 0, 512, 0);
    grd.addColorStop(0, '#c1a273'); grd.addColorStop(0.45, '#d0b98f'); grd.addColorStop(1, '#b99160');
    g.fillStyle = grd; g.fillRect(0, 0, 512, 256);
    g.strokeStyle = 'rgba(65,42,21,0.16)';
    for (var y = 20; y < 256; y += 31) { g.beginPath(); for (var x = 0; x <= 512; x += 16) { var wv = Math.sin(x * 0.035 + y * 0.04) * 3; if (x === 0) g.moveTo(x, y + wv); else g.lineTo(x, y + wv); } g.stroke(); }
    var t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(2, 2); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; return t;
  }
  function environmentTexture() {
    var c = document.createElement('canvas'); c.width = 1024; c.height = 512; var g = c.getContext('2d');
    var grad = g.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.55, '#edf0f4'); grad.addColorStop(1, '#d5d0c2');
    g.fillStyle = grad; g.fillRect(0, 0, 1024, 512);
    g.fillStyle = 'rgba(255,255,255,0.9)'; g.fillRect(120, 72, 280, 92); g.fillRect(690, 112, 190, 70);
    var t = new THREE.CanvasTexture(c); t.mapping = THREE.EquirectangularReflectionMapping; t.colorSpace = THREE.SRGBColorSpace; return t;
  }

  function initMaterials() {
    mats.subfloor = new THREE.MeshStandardMaterial({ color: 0xc4a97a, roughness: 0.85, map: woodTexture() });
    mats.membrane = new THREE.MeshStandardMaterial({ color: 0xe8662a, roughness: 0.76, map: membraneTexture() });
    mats.stud = new THREE.MeshStandardMaterial({ color: 0xf07834, roughness: 0.66 });
    mats.duo = new THREE.MeshStandardMaterial({ color: 0x1d349a, roughness: 0.58, metalness: 0.04 });
    mats.cable = new THREE.MeshStandardMaterial({ color: 0x242229, roughness: 0.38, metalness: 0.08 });
    mats.acrylic = new THREE.MeshPhysicalMaterial({ color: 0xf9fbff, roughness: 0.24, clearcoat: 0.85, clearcoatRoughness: 0.22 });
    mats.basin = new THREE.MeshStandardMaterial({ color: 0xdce6ef, roughness: 0.24 });
    mats.chrome = new THREE.MeshPhysicalMaterial({ color: 0xd5dbe4, roughness: 0.18, metalness: 0.84, clearcoat: 0.55 });
    mats.wood = new THREE.MeshStandardMaterial({ color: 0xb17b48, roughness: 0.64 });
    mats.counter = new THREE.MeshPhysicalMaterial({ color: 0xf1f2ee, roughness: 0.34, clearcoat: 0.35 });
  }

  function init(el) {
    container = el;
    reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var w = el.clientWidth || 360, h = el.clientHeight || 300;

    initMaterials();
    scene = new THREE.Scene();
    scene.environment = environmentTexture();

    camera = new THREE.PerspectiveCamera(32, w / h, 0.1, 100);
    fitCamera();

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(w, h);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.04;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    el.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xc9c4b7, 1.05));
    key = new THREE.DirectionalLight(0xffffff, 2.6);
    key.position.set(6, 12, 7); key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1; key.shadow.camera.far = 44;
    key.shadow.camera.left = -9; key.shadow.camera.right = 9; key.shadow.camera.top = 9; key.shadow.camera.bottom = -9;
    key.shadow.bias = -0.0004; key.shadow.normalBias = 0.025; scene.add(key);
    var rim = new THREE.DirectionalLight(0xcfe0ff, 1.0); rim.position.set(-7, 6, -6); scene.add(rim);

    floorGroup = new THREE.Group(); scene.add(floorGroup);

    subMesh = new THREE.Mesh(new THREE.BoxGeometry(W + 0.6, 0.5, D + 0.6), mats.subfloor);
    subMesh.position.y = -0.5; subMesh.receiveShadow = true; floorGroup.add(subMesh);

    duoMesh = new THREE.Mesh(new THREE.BoxGeometry(W + 0.12, 0.16, D + 0.12), mats.duo);
    duoMesh.position.y = -0.04; duoMesh.visible = false; duoMesh.castShadow = true; duoMesh.receiveShadow = true; floorGroup.add(duoMesh);

    memTex = membraneTexture(); mats.membrane.map = memTex;
    memMesh = new THREE.Mesh(new THREE.BoxGeometry(W, 0.12, D), mats.membrane);
    memMesh.position.y = 0.06; memMesh.receiveShadow = true; floorGroup.add(memMesh);

    fixturesGroup = new THREE.Group(); floorGroup.add(fixturesGroup);
    cableGroup = new THREE.Group(); floorGroup.add(cableGroup);

    buildStuds();

    window.addEventListener('resize', onResize);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { inView = e[0].isIntersecting; inView ? start() : stop(); }, { threshold: 0.01 }).observe(el);
    }
    mounted = true;
    loadModels();
    renderOnce();
    start();
  }

  /* ---------- instanced 3D studs ---------- */
  function buildStuds() {
    if (!floorGroup) return;
    if (studMesh) { floorGroup.remove(studMesh); studMesh.geometry.dispose(); studMesh = null; }
    var sx = 0.25 * UNIT, sz = 0.25 * UNIT;
    var cols = Math.max(1, Math.floor((W - sx) / sx)), rows = Math.max(1, Math.floor((D - sz) / sz));
    var count = cols * rows; if (count > 4000) return;
    var r = Math.min(sx, sz) * 0.19;
    var geo = new THREE.CylinderGeometry(r * 0.86, r, 0.05, 14);
    studMesh = new THREE.InstancedMesh(geo, mats.stud, count);
    studMesh.castShadow = true; studMesh.receiveShadow = true;
    var m = new THREE.Matrix4(), n = 0;
    for (var ix = 0; ix < cols; ix++) for (var iz = 0; iz < rows; iz++) {
      m.makeTranslation(-W / 2 + sx + ix * sx, MEM_TOP + 0.026, -D / 2 + sz + iz * sz); studMesh.setMatrixAt(n++, m);
    }
    studMesh.instanceMatrix.needsUpdate = true; floorGroup.add(studMesh);
  }

  /* ---------- GLB fixture models ---------- */
  function loadModels() {
    if (modelsRequested) return;
    var urls = window.HFKB_MODEL_URLS; if (!urls) return;
    modelsRequested = true;
    loader = new GLTFLoader();
    Object.keys(urls).forEach(function (k) {
      loader.load(urls[k], function (gltf) {
        var root = gltf.scene;
        root.traverse(function (o) { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; if (o.material) { o.material.side = THREE.FrontSide; o.material.needsUpdate = true; } } });
        modelCache[k] = root;
        if (lastData) { buildFixtures(lastData); renderOnce(); }
      }, undefined, function () { modelCache[k] = null; });
    });
  }

  // place a GLB scaled to the world footprint (fw x fd), capped in height. opts: {rotationY, offsetX, offsetZ} in world units.
  function addFixtureModel(group, kind, fw, fd, opts) {
    var src = modelCache[kind]; if (!src) return false;
    var model = src.clone(true);
    var cfg = MODEL_CONFIG[kind] || {}; opts = opts || {};
    var sw = W / 10;                                  // demo authored at WORLD_W=10
    var box = new THREE.Box3().setFromObject(model), size = box.getSize(new THREE.Vector3());
    if (!size.x || !size.y || !size.z) return false;
    // orient so the model's long footprint axis aligns with the footprint's long axis (gentler stretch)
    var footWide = fw >= fd, modelWide = size.x >= size.z;
    if (footWide !== modelWide) model.rotation.y = Math.PI / 2;
    model.rotation.y += (MODEL_FACE[kind] || 0) + ((opts.inwardZ || 1) < 0 ? Math.PI : 0);  // face the room
    // re-measure world AABB after rotation; center on x/z, seat base on the floor (local y=0)
    box = new THREE.Box3().setFromObject(model); size = box.getSize(new THREE.Vector3());
    var c = box.getCenter(new THREE.Vector3()), mn = box.min;
    model.position.x -= c.x; model.position.z -= c.z; model.position.y -= mn.y;
    // NON-UNIFORM fill: stretch x/z to the footprint the builder set (so an 8ft island reads 8ft);
    // height tracks the smaller ratio so a long fixture stays a sensible height. Wrap in an
    // axis-aligned holder so the stretch is along WORLD axes regardless of the model's rotation.
    var f = cfg.scale || 1.0;
    var sX = (fw / size.x) * f, sZ = (fd / size.z) * f, sY = Math.min(sX, sZ);
    var holder = new THREE.Group();
    holder.add(model);
    holder.scale.set(sX, sY, sZ);
    holder.position.x = (opts.offsetX || 0) + (cfg.offsetX || 0) * sw;
    holder.position.z = (opts.offsetZ || 0) + (cfg.offsetZ || 0) * sw;
    group.add(holder);
    return true;
  }

  /* ---------- procedural geometry helpers ---------- */
  function addBox(p, w, h, d, mat, x, y, z) { var m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat); m.position.set(x, y + h / 2, z); m.castShadow = true; m.receiveShadow = true; p.add(m); return m; }
  function addCylinder(p, r, h, mat, x, y, z, seg, sx, sz) { var m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, seg || 20), mat); m.scale.x = sx || 1; m.scale.z = sz || 1; m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true; p.add(m); return m; }
  function addRounded(p, w, d, h, r, mat, x, y, z) { var m = new THREE.Mesh(roundedPrism(w, d, h, r), mat); m.position.set(x, y, z); m.castShadow = true; m.receiveShadow = true; p.add(m); return m; }
  function roundedPrism(w, d, h, r) {
    r = Math.max(0.01, Math.min(r, w * 0.28, d * 0.28));
    var x = w / 2, z = d / 2, sh = new THREE.Shape();
    sh.moveTo(-x + r, -z); sh.lineTo(x - r, -z); sh.quadraticCurveTo(x, -z, x, -z + r);
    sh.lineTo(x, z - r); sh.quadraticCurveTo(x, z, x - r, z); sh.lineTo(-x + r, z);
    sh.quadraticCurveTo(-x, z, -x, z - r); sh.lineTo(-x, -z + r); sh.quadraticCurveTo(-x, -z, -x + r, -z);
    var geo = new THREE.ExtrudeGeometry(sh, { depth: h, bevelEnabled: true, bevelThickness: Math.min(0.025, h * 0.2), bevelSize: Math.min(0.025, r * 0.35), bevelSegments: 3, curveSegments: 8 });
    geo.rotateX(-Math.PI / 2); geo.computeVertexNormals(); return geo;
  }

  /* ---------- per-fixture builders (GLB-first, procedural fallback) ---------- */
  /* ---------- per-fixture builders (GLB-first, procedural fallback). iz = inward z (+1 back/top wall, -1 front/bottom) ---------- */
  function createTub(g, w, d, iz) {
    if (addFixtureModel(g, 'tub', w, d, { inwardZ: iz })) return;
    addRounded(g, w * 0.95, d * 0.9, 0.68, Math.min(w, d) * 0.08, mats.acrylic, 0, 0, 0);
    addBox(g, w * 0.72, 0.04, d * 0.52, mats.basin, 0, 0.7, 0);
    addCylinder(g, 0.04, 0.02, mats.chrome, -w * 0.3, 0.82, -d * 0.25 * iz);
  }
  function createToilet(g, w, d, iz) {
    if (addFixtureModel(g, 'toilet', w, d, { inwardZ: iz })) return;
    addCylinder(g, Math.min(w, d) * 0.18, 0.32, mats.acrylic, 0, 0.18, -d * 0.05 * iz, 28, 1.25, 0.9);
    var bowl = new THREE.Mesh(new THREE.TorusGeometry(Math.min(w, d) * 0.22, 0.04, 12, 40), mats.acrylic);
    bowl.rotation.x = Math.PI / 2; bowl.scale.x = 1.22; bowl.position.set(0, 0.54, -d * 0.06 * iz); bowl.castShadow = true; g.add(bowl);
    addRounded(g, w * 0.62, d * 0.22, 0.42, Math.min(w, d) * 0.05, mats.acrylic, 0, 0.16, d * 0.31 * iz);
  }
  function createVanity(g, w, d, iz) {
    if (addFixtureModel(g, 'vanity', w, d, { inwardZ: iz })) return;
    addRounded(g, w * 0.9, d * 0.78, 0.58, Math.min(w, d) * 0.04, mats.wood, 0, 0, 0);
    addRounded(g, w * 0.98, d * 0.9, 0.11, Math.min(w, d) * 0.045, mats.counter, 0, 0.58, 0);
    addCylinder(g, 0.026, 0.16, mats.chrome, 0, 0.78, d * 0.2 * iz);
    var mirror = new THREE.Mesh(new THREE.BoxGeometry(w * 0.62, 0.045, 0.04), mats.chrome);
    mirror.position.set(0, 1.08, -d * 0.47 * iz); mirror.castShadow = true; mirror.receiveShadow = true; g.add(mirror);
  }
  function createShower(g, w, d, iz) {
    addRounded(g, w * 0.94, d * 0.9, 0.22, Math.min(w, d) * 0.06, mats.acrylic, 0, 0, 0);
    addRounded(g, w * 0.72, d * 0.68, 0.035, Math.min(w, d) * 0.045, mats.basin, 0, 0.225, 0);
    if (!addFixtureModel(g, 'shower', w * 0.34, d * 0.34, { inwardZ: iz })) { addCylinder(g, 0.05, 0.02, mats.chrome, w * 0.18, 0.27, -d * 0.08 * iz); }
    addCylinder(g, 0.022, 0.62, mats.chrome, w * 0.36, 0.51, -d * 0.35 * iz);
  }
  function createCabinet(g, w, d, kind, iz) {
    if (addFixtureModel(g, kind || 'cabinets', w, d, { inwardZ: iz })) return;
    addRounded(g, w * 0.94, d * 0.76, 0.62, Math.min(w, d) * 0.035, mats.wood, 0, 0, 0);
    addRounded(g, w, d * 0.86, 0.12, Math.min(w, d) * 0.045, mats.counter, 0, 0.62, 0);
  }

  function buildFixtures(data) {
    clearGroup(fixturesGroup);
    var rects = data.fixtures || [];
    for (var i = 0; i < rects.length; i++) {
      var r = rects[i];
      var fw = fxScaleX(r.w), fd = fxScaleZ(r.h);
      var iz = (r.wall === 'bottom') ? -1 : 1;   // face the room interior
      var g = new THREE.Group(); g.position.set(mapX(r.x + r.w / 2), MEM_TOP + 0.02, mapZ(r.y + r.h / 2)); fixturesGroup.add(g);
      if (r.k === 'tub') createTub(g, fw, fd, iz);
      else if (r.k === 'toilet') createToilet(g, fw, fd, iz);
      else if (r.k === 'vanity') createVanity(g, fw, fd, iz);
      else if (r.k === 'shower') createShower(g, fw, fd, iz);
      else if (r.k === 'island') createCabinet(g, fw, fd, 'island', iz);
      else createCabinet(g, fw, fd, 'cabinets', iz);
    }
  }

  /* ---------- cable: crisp cylinder segments + sphere joints ---------- */
  function buildCable(data) {
    clearGroup(cableGroup);
    var pts = (data.built ? (data.cable || []) : []); if (pts.length < 2) return;
    var R = Math.max(0.012, Math.min(0.05, 0.03 * currentSF)), y = MEM_TOP + 0.04;
    var world = pts.map(function (p) { return new THREE.Vector3(mapX(p[0]), y, mapZ(p[1])); });
    var jointGeo = new THREE.SphereGeometry(R * 1.15, 12, 8);
    for (var i = 0; i < world.length - 1; i++) {
      var a = world[i], b = world[i + 1], dx = b.x - a.x, dz = b.z - a.z, len = Math.sqrt(dx * dx + dz * dz);
      if (len < 0.001) continue;
      var cyl = new THREE.Mesh(new THREE.CylinderGeometry(R, R, len, 12), mats.cable);
      cyl.position.set((a.x + b.x) / 2, y, (a.z + b.z) / 2);
      if (Math.abs(dx) > Math.abs(dz)) cyl.rotation.z = Math.PI / 2; else cyl.rotation.x = Math.PI / 2;
      cyl.castShadow = true; cableGroup.add(cyl);
    }
    for (var j = 0; j < world.length; j++) { var s = new THREE.Mesh(jointGeo, mats.cable); s.position.copy(world[j]); s.castShadow = true; cableGroup.add(s); }
  }

  function onResize() { if (!container || !renderer) return; var w = container.clientWidth || 360, h = container.clientHeight || 300; camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h); renderOnce(); }
  function clearGroup(g) { for (var i = g.children.length - 1; i >= 0; i--) { var c = g.children[i]; g.remove(c); c.traverse(function (o) { if (o.geometry) o.geometry.dispose(); }); } }

  window.HFKBScene = {
    mount: function (el) { if (!mounted) { try { init(el); } catch (e) { /* WebGL unavailable */ } } },
    update: function (data) {
      if (!mounted) return;
      data = data || {}; lastData = data;
      setFloorSize(data.roomLong, data.roomShort);
      duoMesh.visible = !!data.coldFam && !!data.built;
      buildFixtures(data); buildCable(data); renderOnce();
    },
    dispose: function () { stop(); if (renderer) { renderer.dispose(); if (renderer.domElement && renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement); } mounted = false; }
  };

  function renderOnce() { if (renderer) renderer.render(scene, camera); }
  function frame(t) { if (!running) return; raf = requestAnimationFrame(frame); var dt = lastT ? (t - lastT) / 1000 : 0; lastT = t; if (!reduced) { spin += dt * 0.18; floorGroup.rotation.y = Math.sin(spin) * 0.32; } renderer.render(scene, camera); }
  function start() { if (running || !mounted || reduced || !inView) { renderOnce(); return; } running = true; lastT = 0; raf = requestAnimationFrame(frame); }
  function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = null; }

  function tryMount() { var el = document.getElementById('hfkb-3d'); if (el) { window.HFKBScene.mount(el); document.dispatchEvent(new CustomEvent('hfkb:scene-ready')); } }
  if (document.readyState !== 'loading') tryMount(); else document.addEventListener('DOMContentLoaded', tryMount);
})();
